<script setup>
import { Head, Link, useForm } from '@inertiajs/vue3';
import { ref } from 'vue';

import AuthenticationCard from '@/Components/AuthenticationCard.vue';
import AuthenticationCardSocial from '@/Components/AuthenticationCardSocial.vue';
import GlobalFooter from '@/Components/GlobalFooter.vue';

import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import SvgIcon from '@/Components/SvgIcon.vue';

defineProps({
    canResetPassword: Boolean,
    status: String,
});

const form = useForm({
    login: '',
    password: '',
    remember: false,
});

const showPassword = ref(false);

const toggleShowPassword = () => {
    showPassword.value = !showPassword.value;
};

const submit = () => {
    form.transform(data => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('login'), {
        onFinish: () => form.reset('password'),
    });
};
</script>

<template>
    <Head :title="$t('auth.loginTitle')" />

    <AuthenticationCard>
        <div class="mb-8">
            <h2 class="h2 text-white mb-3.5">{{ $t('auth.loginTitle') }}</h2>
            <p class="text-base text-grayscale-400">{{ $t('auth.loginDescription') }}</p>
            <div v-if="$page.props.status">
                <p class="text-base text-grayscale-400 mb-4 mt-4">{{ $page.props.status }}</p>
            </div>
        </div>

        <AuthenticationCardSocial />

        <div class="flex items-center justify-center my-6 auth-divider">
            <span class="px-5 text-grayscale-400 font-bold">{{ $t('auth.divider') }}</span>
        </div>

        <form @submit.prevent="submit">
            <div class="mb-6">
                <InputLabel for="login" :value="$t('auth.loginLabelName')" />
                <TextInput
                    id="login"
                    v-model="form.login"
                    type="text"
                    class="block w-full"
                    required
                    autofocus
                    autocomplete="username"
                    :placeholder="$t('auth.loginPlaceholderName')"
                />
                <InputError class="mt-2" :message="form.errors.login" />
            </div>

            <div class="mb-2">
                <InputLabel for="password" :value="$t('auth.loginLabelPassword')" />
                <div class="relative">
                    <TextInput
                        id="password"
                        v-model="form.password"
                        :type="showPassword ? 'text' : 'password'"
                        class="block w-full pr-10"
                        required
                        autocomplete="new-password"
                        :placeholder="$t('auth.loginPlaceholderPassword')"
                    />
                    <button type="button" @click="toggleShowPassword" class="absolute inset-y-0 right-0 pr-6 flex items-center text-white">
                        <SvgIcon v-if="showPassword" name="eye-closed" width="24" height="24" />
                        <SvgIcon v-else name="eye" width="24" height="24" />
                    </button>
                </div>
                <InputError class="mt-2" :message="form.errors.password" />
            </div>

            <div class="flex items-center justify-end mb-10">
                <Link v-if="canResetPassword" :href="route('password.request')" class="font-bold text-primary-500 hover:underline focus:outline-0 focus:shadow-none">
                    {{ $t('auth.loginForgotPassword') }}
                </Link>
            </div>

            <PrimaryButton class="w-full mb-6" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">{{ $t('auth.login') }}</PrimaryButton>

            <div class="flex items-center justify-center gap-1 text-base mb-2">
                <span class="text-white">{{ $t('auth.loginAccount') }}</span>
                <Link :href="route('register')" class="font-bold text-primary-500 hover:underline focus:outline-0 focus:shadow-none">
                    {{ $t('auth.loginCreate') }}
                </Link>
            </div>
        </form>

        <template #footer>
            <GlobalFooter />
        </template>
    </AuthenticationCard>
</template>
